import { useEffect, useRef, useState } from "react";

/**
 * Captures Chrome/Edge's `beforeinstallprompt` (fires once, before the browser would otherwise show
 * its own install UI) so a page can offer its own button and trigger the native prompt on demand.
 * `canInstall` is false on Safari/iOS and on any browser that has already installed the app or
 * decided not to offer it — there is no other reliable signal for "can this be installed".
 */
export function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(false);
  const deferredEvent = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      deferredEvent.current = event;
      setCanInstall(true);
    };
    const onInstalled = () => {
      deferredEvent.current = null;
      setCanInstall(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
    const event = deferredEvent.current;
    if (!event) return "unavailable";
    deferredEvent.current = null;
    setCanInstall(false);
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  }

  return { canInstall, promptInstall };
}
